import React, { useEffect } from "react";
import { useI18n } from "@/lib/i18n";

/* Highlight + scroll to a record opened by Luna (focusId). Rows need data-rid={id}. */
export const useFocusRecord = (items, focusId) => {
  useEffect(() => {
    if (!focusId || !items?.length) return;
    const el = document.querySelector(`[data-rid="${focusId}"]`);
    if (el) { el.scrollIntoView({ behavior: "smooth", block: "center" }); }
  }, [items, focusId]);
};
export const focusCls = (id, focusId) => (id && id === focusId ? " ring-2 ring-cyan-400/70 shadow-[0_0_24px_rgba(0,240,255,.25)]" : "");

export const Badge = ({ children, color = "cyan", testid }) => {
  const map = {
    cyan: "bg-cyan-500/15 border-cyan-400/30 text-cyan-300",
    green: "bg-emerald-500/15 border-emerald-400/30 text-emerald-300",
    amber: "bg-amber-500/15 border-amber-400/30 text-amber-300",
    red: "bg-red-500/15 border-red-400/30 text-red-300",
    gray: "bg-white/5 border-white/15 text-white/60",
    blue: "bg-blue-500/15 border-blue-400/30 text-blue-300",
    purple: "bg-purple-500/15 border-purple-400/30 text-purple-300",
  };
  return <span data-testid={testid} className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[0.68rem] font-mono ${map[color] || map.cyan}`}>{children}</span>;
};

export const PanelHead = ({ title, sub, right }) => (
  <div className="flex items-start justify-between mb-5">
    <div>
      <h2 className="text-2xl font-semibold tracking-tight text-white">{title}</h2>
      {sub && <p className="label-eyebrow mt-1">{sub}</p>}
    </div>
    {right}
  </div>
);

export const Empty = ({ text }) => (
  <div className="text-center py-14 text-[color:var(--txt-3)] text-sm" data-testid="empty-state">{text}</div>
);

export const Field = ({ label, ...props }) => (
  <label className="block mb-3">
    <span className="label-eyebrow block mb-1">{label}</span>
    <input {...props} className="w-full bg-black/30 border border-cyan-500/20 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-cyan-400/60" />
  </label>
);

export const Area = ({ label, ...props }) => (
  <label className="block mb-3">
    <span className="label-eyebrow block mb-1">{label}</span>
    <textarea {...props} className="w-full bg-black/30 border border-cyan-500/20 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-cyan-400/60 min-h-[80px]" />
  </label>
);

export const Btn = ({ children, variant = "primary", ...props }) => {
  const v = {
    primary: "bg-cyan-500/20 border-cyan-400/40 text-cyan-200 hover:bg-cyan-500/35",
    ghost: "bg-white/5 border-white/15 text-white/70 hover:text-cyan-300",
    danger: "bg-red-500/15 border-red-400/30 text-red-300 hover:bg-red-500/30",
  }[variant];
  return <button {...props} className={`px-3.5 py-2 rounded-lg border text-sm transition-colors ${v} ${props.className || ""}`}>{children}</button>;
};

/* Compact settings card: one subject per card, balanced two-column grid on desktop. */
export const SettingsCard = ({ title, icon: Icon, right, children, testid, wide = false }) => (
  <section data-testid={testid} className={`panel p-4 sm:p-5 flex flex-col ${wide ? "lg:col-span-2" : ""}`}>
    <header className="flex items-center justify-between gap-2 mb-3 flex-wrap">
      <h3 className="label-eyebrow flex items-center gap-2 m-0">
        {Icon ? <Icon size={13} className="text-cyan-300" /> : null}{title}
      </h3>
      {right}
    </header>
    <div className="flex-1">{children}</div>
  </section>
);

/* Technical detail, collapsed by default — keeps the settings screen calm. */
export const Advanced = ({ label, children, testid }) => {
  const { tx } = useI18n();
  return (
    <details className="mt-3 group" data-testid={testid}>
      <summary className="cursor-pointer text-[0.7rem] text-white/45 hover:text-cyan-200 select-none list-none flex items-center gap-1.5">
        <span className="transition-transform group-open:rotate-90">›</span>{label || tx("Gelişmiş ayrıntılar")}
      </summary>
      <div className="mt-2 space-y-2 text-[0.7rem] text-white/50 leading-relaxed">{children}</div>
    </details>
  );
};
